import { Accordion, AccordionItem } from "@heroui/react"
import { useLingui } from "@lingui/react/macro"
import { createElement } from "react"

import { JavaScript } from "#/components/FileIcon"

import type { Initiator, StackFrame, StackParent } from "../har"
import { KVBox, KVTableHeader } from "#/components/KVTable"

function* stackFrame(stack: StackParent, i = 0): Generator<React.ReactElement> {
  if (stack.callFrames.length) {
    const first = stack.callFrames[0]
    yield (
      <AccordionItem
        key={i}
        textValue={(stack.description ?? first?.functionName) || "<anonymous>"}
        classNames={{
          title: "text-sm",
          trigger: "py-1",
        }}
        title={
          stack.description || (
            <div css="flex items-center gap-2">
              {JavaScript}{" "}
              {first?.functionName ? (
                `${first.functionName}()`
              ) : (
                <span css="opacity-50">{"<anonymous>"}</span>
              )}
            </div>
          )
        }
      >
        <ol>
          {stack.callFrames.map((f, i, list) => (
            <li css="mb-1.5 font-mono text-sm" key={i}>
              {f.functionName || <span css="opacity-50">{"<anonymous>"}</span>}{" "}
              <span>
                <span css="opacity-30">(</span>
                <CallStackURL current={f} previous={list[i - 1]} />
                <span css="opacity-50">:</span>
                <wbr />
                {f.lineNumber}:<wbr />
                {f.columnNumber}
                <span css="opacity-30">)</span>
              </span>
            </li>
          ))}
        </ol>
      </AccordionItem>
    )
  }
  if (stack.parent != null) {
    yield* stackFrame(stack.parent, i + 1)
  }
}

function CallStackURL({
  current,
  previous,
}: {
  current: StackFrame
  previous?: StackFrame
}) {
  const href = current.url
  const isSame = href === previous?.url && href.length > 10
  const text = isSame ? "^" : href

  return (
    <a
      css="break-words text-blue-950 hover:underline dark:text-blue-100"
      href={href}
      rel="noopener noreferrer"
      target="_blank"
      title={isSame ? href : undefined}
    >
      {text}
    </a>
  )
}

export function InitiatorView({ initiator }: { initiator: Initiator }) {
  const { t } = useLingui()
  const header = <KVTableHeader>{t`Initiator`}</KVTableHeader>

  if (
    initiator.type === "parser" ||
    (initiator.type === "script" && !("stack" in initiator))
  ) {
    return (
      <KVBox title={t`Initiator`}>
        {initiator.url}{" "}
        {initiator.lineNumber != null && (
          <span css="ml-1 border-l pl-2">
            <span css="opacity-50">Line</span> {initiator.lineNumber}
          </span>
        )}
      </KVBox>
    )
  } else if (initiator.type === "script") {
    return (
      <div css="mb-6">
        {header}
        <div css="rounded-large bg-content1 px-4 py-2 shadow-small">
          {createElement(Accordion, {
            items: [],
            selectionMode: "multiple",
            isCompact: true,
            children: [...stackFrame(initiator.stack)] as any,
          })}
        </div>
      </div>
    )
  }

  return null
}
